import { Library, ScanLine } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { CoverCard } from '../components/CoverCard';
import { EmptyState } from '../components/EmptyState';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Panel } from '../components/ui/panel';
import { Progress } from '../components/ui/progress';
import { CoverSkeleton } from '../components/ui/skeleton';
import { IssueGapGrid, type GapGridIssue } from '../features/series/IssueGapGrid';

const COLOR_TOKENS = [
  { name: 'background', variable: '--pv-background', usage: 'Seitenhintergrund' },
  { name: 'surface', variable: '--pv-surface', usage: 'Karten, Sidebar' },
  { name: 'surface-raised', variable: '--pv-surface-raised', usage: 'Felder, Hover' },
  { name: 'border', variable: '--pv-border', usage: 'Panel-Kanten' },
  { name: 'foreground', variable: '--pv-foreground', usage: 'Text' },
  { name: 'muted', variable: '--pv-muted', usage: 'Sekundärtext' },
  { name: 'accent', variable: '--pv-accent', usage: 'Aktion, aktiv' },
  { name: 'accent-strong', variable: '--pv-accent-strong', usage: 'Hover der Aktion' },
  { name: 'success', variable: '--pv-success', usage: 'vollständig' },
  { name: 'danger', variable: '--pv-danger', usage: 'Fehler, löschen' },
];

const SAMPLE_COLLECTION = [
  { title: 'Saga', number: '12', subtitle: 'Image · 2013' },
  { title: 'Tim und Struppi', number: '7', subtitle: 'Carlsen · Album' },
  { title: 'Batman', number: '404', subtitle: 'DC · 1987', quantity: 2 },
  { title: 'Berserk', number: '3', subtitle: 'Panini Manga' },
  { title: 'Hellboy', number: '1', subtitle: 'Dark Horse' },
  { title: 'Spirou und Fantasio', number: '38', subtitle: 'Carlsen · Album' },
  { title: 'Sandman', number: '19', subtitle: 'Vertigo · 1990' },
  { title: 'Monster', number: '5', subtitle: 'Manga · Taschenbuch' },
];

/** Issues 1-24 of a run, with a handful still missing. */
const MISSING = new Set(['4', '5', '11', '17', '18', '23']);

const SAMPLE_ISSUES: GapGridIssue[] = Array.from({ length: 24 }, (_, index) => {
  const number = String(index + 1);
  return {
    number,
    owned: !MISSING.has(number),
    variantCount: number === '1' ? 3 : undefined,
  };
});

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-4">
      <div>
        <h2 className="text-2xl text-foreground">{title}</h2>
        {hint ? <p className="mt-1 max-w-2xl text-sm text-muted">{hint}</p> : null}
      </div>
      {children}
    </section>
  );
}

/**
 * Working page for the visual language. Not linked from the navigation; it
 * exists so the design can be reviewed and corrected before the real screens
 * are built on top of these components.
 */
export function DesignPage() {
  const { t } = useTranslation();

  return (
    <div className="flex max-w-5xl flex-col gap-12">
      <header className="rounded-panel border halftone px-6 py-8">
        <h1 className="text-4xl text-foreground">{t('design.title')}</h1>
        <p className="mt-3 max-w-2xl text-sm text-muted">{t('design.intro')}</p>
      </header>

      <Section title={t('design.colors')} hint={t('design.colorsHint')}>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {COLOR_TOKENS.map((token) => (
            <Panel key={token.name} className="overflow-hidden">
              <div className="h-16 w-full" style={{ backgroundColor: `var(${token.variable})` }} />
              <div className="border-t px-3 py-2">
                <p className="text-xs font-medium text-foreground">{token.name}</p>
                <p className="text-[0.7rem] text-muted">{token.usage}</p>
              </div>
            </Panel>
          ))}
        </div>
      </Section>

      <Section title={t('design.typography')} hint={t('design.typographyHint')}>
        <Panel className="flex flex-col gap-4 p-6">
          <p className="font-display text-5xl leading-none text-foreground uppercase">Sammlung</p>
          <p className="font-display text-3xl leading-none text-foreground uppercase">
            Serie · Band 3
          </p>
          <p className="text-base text-foreground">
            Fließtext in Inter: 34 von 50 Ausgaben vorhanden, zuletzt ergänzt am 12. März.
          </p>
          <p className="text-sm text-muted">Sekundärtext für Metadaten, Hinweise und Hilfetexte.</p>
          <p className="text-xs tracking-wide text-muted uppercase">Label · Verlag · Format</p>
        </Panel>
      </Section>

      <Section title={t('design.components')}>
        <Panel className="flex flex-col gap-6 p-6">
          <div className="flex flex-wrap items-center gap-3">
            <Button>Ausgabe anlegen</Button>
            <Button variant="surface">Bearbeiten</Button>
            <Button variant="ghost">Abbrechen</Button>
            <Button variant="danger">Löschen</Button>
            <Button size="sm" variant="surface">
              Klein
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="accent">laufend</Badge>
            <Badge variant="success">vollständig</Badge>
            <Badge>Panini</Badge>
            <Badge variant="outline">Taschenbuch</Badge>
            <Badge variant="danger">Lücke</Badge>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Input placeholder="Serie suchen …" />
            <Progress value={34} max={50} label="34 von 50" />
          </div>
        </Panel>
      </Section>

      <Section title={t('design.sketchCollection')} hint={t('design.sketchCollectionHint')}>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {SAMPLE_COLLECTION.map((item) => (
            <CoverCard
              key={`${item.title}-${item.number}`}
              title={item.title}
              number={item.number}
              subtitle={item.subtitle}
              quantity={item.quantity}
              onClick={() => undefined}
            />
          ))}
        </div>
        <p className="text-xs text-muted">{t('design.motionNote')}</p>
      </Section>

      <Section title={t('design.sketchSeries')} hint={t('design.sketchSeriesHint')}>
        <Panel className="flex flex-col gap-6 p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 className="text-2xl text-foreground">Hellboy</h3>
              <p className="mt-1 text-sm text-muted">Dark Horse · 1994–2019 · Heft</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <Badge variant="accent">laufend</Badge>
                <Badge>Dark Horse</Badge>
                <Badge variant="outline">englisch</Badge>
              </div>
            </div>
            <Button variant="surface" size="sm">
              Gesamtzahl ändern
            </Button>
          </div>

          <IssueGapGrid seriesTitle="Hellboy" issues={SAMPLE_ISSUES} expectedCount={24} />
        </Panel>
      </Section>

      <Section title={t('design.emptyStates')}>
        <div className="grid gap-4 lg:grid-cols-2">
          <EmptyState
            icon={Library}
            title="Noch keine Hefte"
            description="Leg die erste Serie an oder scanne einen Barcode, dann füllt sich das Regal."
            action={<Button size="sm">Serie anlegen</Button>}
          />
          <EmptyState
            icon={ScanLine}
            title="Kein Treffer"
            description="Zu diesem Barcode hat keine Quelle etwas gefunden. Du kannst das Heft von Hand erfassen."
            action={
              <Button size="sm" variant="surface">
                Manuell erfassen
              </Button>
            }
          />
        </div>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {Array.from({ length: 4 }, (_, index) => (
            <CoverSkeleton key={index} />
          ))}
        </div>
      </Section>
    </div>
  );
}
